import type { MessageDescriptor } from "react-intl";
import { ListingState, type ListingDetailFragment } from "@/graphql/generated/operations";

// Zustand und Zustandswechsel einer Ausschreibung
// (@external-adr GENESIS-0070-a-listing-is-a-kind-of-project-not-a-shared-item §5).
//
// OB ein Wechsel angeboten wird, entscheidet allein `Listing.actions`: genesis rechnet es mit
// denselben Prüfungen aus wie die Mutation (§6). Der Client kennt deshalb keinen Zustandsautomaten
// und keine Rollen; diese Tabelle sagt nur, WOHIN ein gemeldeter Wechsel führt und wie er heißt.

export type ListingActions = ListingDetailFragment["actions"];

export type ListingTransition = "publish" | "reopen" | "withdraw" | "close" | "conclude";

export interface ListingTransitionSpec {
  transition: ListingTransition;
  target: ListingState;
  allowed: (actions: ListingActions) => boolean;
  /**
   * Führt nach OPEN. Liegt die Frist dann in der Vergangenheit, schlösse sie die Ausschreibung sofort
   * wieder; genesis verlangt deshalb, sie in derselben Anfrage zu verschieben.
   */
  opens: boolean;
}

// Reihenfolge = Reihenfolge der Knöpfe: der Schritt nach vorn zuerst, dann die zurück.
export const LISTING_TRANSITIONS: readonly ListingTransitionSpec[] = [
  { transition: "publish", target: ListingState.Open, allowed: (a) => a.canPublish, opens: true },
  { transition: "reopen", target: ListingState.Open, allowed: (a) => a.canReopen, opens: true },
  { transition: "close", target: ListingState.Closed, allowed: (a) => a.canClose, opens: false },
  { transition: "conclude", target: ListingState.Concluded, allowed: (a) => a.canConclude, opens: false },
  { transition: "withdraw", target: ListingState.Draft, allowed: (a) => a.canWithdraw, opens: false },
];

export function availableTransitions(actions: ListingActions | null | undefined): ListingTransitionSpec[] {
  if (!actions) return [];
  return LISTING_TRANSITIONS.filter((spec) => spec.allowed(actions));
}

/** Liegt die Frist nicht in der Zukunft? Keine Frist ist nie abgelaufen. */
export function deadlinePassed(deadline: string | null | undefined, now: number = Date.now()): boolean {
  if (!deadline) return false;
  const at = Date.parse(deadline);
  return !Number.isNaN(at) && at <= now;
}

export const LISTING_STATES: readonly ListingState[] = [
  ListingState.Draft,
  ListingState.Open,
  ListingState.Closed,
  ListingState.Concluded,
];

export const LISTING_STATE_LABELS: Record<ListingState, MessageDescriptor> = {
  [ListingState.Draft]: { id: "share.listings.state.draft", defaultMessage: "Entwurf" },
  [ListingState.Open]: { id: "share.listings.state.open", defaultMessage: "Offen" },
  [ListingState.Closed]: { id: "share.listings.state.closed", defaultMessage: "Geschlossen" },
  [ListingState.Concluded]: { id: "share.listings.state.concluded", defaultMessage: "Abgeschlossen" },
};

export const LISTING_TRANSITION_LABELS: Record<ListingTransition, MessageDescriptor> = {
  publish: { id: "share.listings.action.publish", defaultMessage: "Veröffentlichen" },
  reopen: { id: "share.listings.action.reopen", defaultMessage: "Wieder öffnen" },
  close: { id: "share.listings.action.close", defaultMessage: "Schließen" },
  conclude: { id: "share.listings.action.conclude", defaultMessage: "Abschließen" },
  withdraw: { id: "share.listings.action.withdraw", defaultMessage: "Zurückziehen" },
};

// Was ein Wechsel zurück oder zu bewirkt, bevor er bestätigt wird. Veröffentlichen und Wiederöffnen
// fragen nicht nach: sie sind der Zweck der Seite und jederzeit umkehrbar.
export const LISTING_TRANSITION_CONSEQUENCES: Partial<Record<ListingTransition, MessageDescriptor>> = {
  withdraw: {
    id: "share.listings.confirm.withdraw",
    defaultMessage:
      "Die Ausschreibung wird wieder zum Entwurf. Gruppen und Öffentlichkeit sehen sie sofort nicht mehr, Inhalt und Mitglieder bleiben.",
  },
  close: {
    id: "share.listings.confirm.close",
    defaultMessage: "Es gehen keine neuen Bewerbungen mehr ein. Du kannst die Ausschreibung später wieder öffnen.",
  },
  conclude: {
    id: "share.listings.confirm.conclude",
    defaultMessage: "Das Verfahren ist beendet. Ab jetzt läuft die Aufbewahrungsfrist.",
  },
};
